import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import CPTip from '../../../Utils/CPTip'
import {
  insertTemplate,
  upsertTemplateTranslation,
  getTemplateById,
  updateTemplate
} from '../../../../services/notificationRules'

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
}

// Quill toolbar is hidden in read-only mode via this minimal config
const QUILL_MODULES_READONLY = { toolbar: false }

const AVAILABLE_VARS = [
  '{{recipient_email}}',
  '{{doc_filename}}',
  '{{doc_expiry}}'
]

const EMPTY_TRANSLATION = { subject: '', body_html: '', body_text: '' }

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

// mode: 'create' | 'edit' | 'preview'
function TranslationForm({ value, onChange, quillRef, readOnly, t }) {
  const insertVar = (variable) => {
    const editor = quillRef.current?.getEditor()
    if (editor) {
      const range = editor.getSelection(true)
      editor.insertText(range.index, variable)
    }
  }

  return (
    <Box>
      <TextField
        fullWidth
        size='small'
        label={t('Subject')}
        value={value.subject}
        onChange={(e) =>
          !readOnly && onChange({ ...value, subject: e.target.value })
        }
        sx={{ mb: 2 }}
        placeholder='e.g. Document {{doc_filename}} expires on {{doc_expiry}}'
        InputProps={{ readOnly }}
      />

      {!readOnly && (
        <Box sx={{ mb: 1 }}>
          <Typography variant='caption' color='text.secondary' sx={{ mr: 1 }}>
            {t('Insert variable')}:
          </Typography>
          {AVAILABLE_VARS.map((v) => (
            <Chip
              key={v}
              label={v}
              size='small'
              sx={{
                mr: 0.5,
                mb: 0.5,
                cursor: 'pointer',
                fontFamily: 'monospace'
              }}
              onClick={() => insertVar(v)}
            />
          ))}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{ mb: 0.5 }}
        >
          {t('Email body')}
        </Typography>
        <ReactQuill
          ref={quillRef}
          theme='snow'
          value={value.body_html}
          onChange={(html) =>
            !readOnly && onChange({ ...value, body_html: html })
          }
          modules={readOnly ? QUILL_MODULES_READONLY : QUILL_MODULES}
          readOnly={readOnly}
          style={{
            minHeight: 180,
            backgroundColor: readOnly ? 'transparent' : undefined
          }}
        />
      </Box>

      <TextField
        fullWidth
        size='small'
        multiline
        minRows={3}
        label={t('Plaintext fallback')}
        value={value.body_text}
        onChange={(e) =>
          !readOnly && onChange({ ...value, body_text: e.target.value })
        }
        helperText={
          !readOnly
            ? t('Shown in email clients that do not support HTML')
            : undefined
        }
        InputProps={{ readOnly }}
      />
    </Box>
  )
}

// Props:
//   open         — bool
//   onClose      — () => void
//   onSaved      — (template) => void   called after create or edit
//   template     — null | { id, description, is_system }
//                  null        → create mode
//                  is_system=0 → edit mode
//                  is_system=1 → preview mode
export default function TemplateEditorDialog({
  open,
  onClose,
  onSaved,
  template,
  isSystem,
  isHidden
}) {
  const { t } = useTranslation()
  const quillEnRef = useRef(null)
  const quillEsRef = useRef(null)

  const isCreate = !template
  const isPreview = template?.is_system === 1
  const isEdit = template && !isPreview
  const readOnly = isPreview

  const [tab, setTab] = useState(0)
  const [description, setDescription] = useState('')
  const [en, setEn] = useState(EMPTY_TRANSLATION)
  const [es, setEs] = useState(EMPTY_TRANSLATION)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // When opening in edit or preview mode, fetch the full template with translations
  useEffect(() => {
    if (!open || !template?.id) return
    setLoading(true)
    getTemplateById(template.id)
      .then((res) => {
        setDescription(res.description || '')
        const translations = res.translations || []
        const enRow = translations.find((tr) => tr.locale === 'en')
        const esRow = translations.find((tr) => tr.locale === 'es')
        setEn(
          enRow
            ? {
                subject: enRow.subject,
                body_html: enRow.body_html,
                body_text: enRow.body_text
              }
            : EMPTY_TRANSLATION
        )
        setEs(
          esRow
            ? {
                subject: esRow.subject,
                body_html: esRow.body_html,
                body_text: esRow.body_text
              }
            : EMPTY_TRANSLATION
        )
      })
      .catch(() => setError(t('Failed to load template')))
      .finally(() => setLoading(false))
  }, [open, template?.id])

  useEffect(() => {
    if (!open) return
    if (!template?.id) {
      // Reset everything for create mode
      setDescription('')
      setEn(EMPTY_TRANSLATION)
      setEs(EMPTY_TRANSLATION)
      setTab(0)
      setError('')
    }
  }, [open, template?.id])

  const validate = () => {
    const enFilled = en.subject.trim() || en.body_html.trim()
    const esFilled = es.subject.trim() || es.body_html.trim()
    if (!enFilled && !esFilled) return t('Please fill in at least one language')
    if (enFilled && !en.subject.trim()) return t('English subject is required')
    if (enFilled && !en.body_html.trim())
      return t('English email body is required')
    if (enFilled && !en.body_text.trim())
      return t('English plaintext fallback is required')
    if (esFilled && !es.subject.trim()) return t('Spanish subject is required')
    if (esFilled && !es.body_html.trim())
      return t('Spanish email body is required')
    if (esFilled && !es.body_text.trim())
      return t('Spanish plaintext fallback is required')
    return null
  }

  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setSaving(true)

    try {
      let templateId

      if (isCreate) {
        const res = await insertTemplate({
          description,
          isSystem: isSystem ? 1 : 0,
          isHidden: isHidden ? 1 : 0
        })
        templateId = res.id
      } else {
        await updateTemplate(template.id, { description })
        templateId = template.id
      }

      const saves = []
      if (en.subject.trim())
        saves.push(upsertTemplateTranslation(templateId, 'en', en))
      if (es.subject.trim())
        saves.push(upsertTemplateTranslation(templateId, 'es', es))
      await Promise.all(saves)

      onSaved({ id: templateId, description })
      handleClose()
    } catch (e) {
      setError(t('An error occurred. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setDescription('')
    setEn(EMPTY_TRANSLATION)
    setEs(EMPTY_TRANSLATION)
    setTab(0)
    setError('')
    onClose()
  }

  const enHasContent = en.subject.trim() || en.body_html.trim()
  const esHasContent = es.subject.trim() || es.body_html.trim()

  const dialogTitle = isPreview
    ? t('Template')
    : isEdit
      ? t('Edit template')
      : t('Create email template')

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {!readOnly && (
              <CPTip
                text={t(
                  'Use {{variable}} syntax to insert dynamic values into the subject and body'
                )}
              />
            )}

            <TextField
              fullWidth
              size='small'
              label={t('Template description')}
              value={description}
              onChange={(e) => !readOnly && setDescription(e.target.value)}
              sx={{ mt: 2, mb: 1 }}
              InputProps={{ readOnly }}
            />

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('English')}
                    {enHasContent && (
                      <Chip
                        label='EN'
                        size='small'
                        color='primary'
                        sx={{ height: 16, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {t('Spanish')}
                    {esHasContent && (
                      <Chip
                        label='ES'
                        size='small'
                        color='primary'
                        sx={{ height: 16, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <TranslationForm
                value={en}
                onChange={setEn}
                quillRef={quillEnRef}
                readOnly={readOnly}
                t={t}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <TranslationForm
                value={es}
                onChange={setEs}
                quillRef={quillEsRef}
                readOnly={readOnly}
                t={t}
              />
            </TabPanel>

            {error && (
              <Typography
                variant='caption'
                color='error'
                sx={{ mt: 1, display: 'block' }}
              >
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {readOnly ? t('Close') : t('Cancel')}
        </Button>
        {!readOnly && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : t('Save template')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
